<template>
  <div
    class="login-page"
    :class="isDarkMode ? 'login-page--dark' : 'login-page--light'"
  >
    <Snackbar :SnackBarComponent="SnackBarComponent" />

    <button
      type="button"
      class="login-page__theme-toggle"
      :aria-label="isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'"
      @click="toggleLoginTheme"
    >
      <v-icon
        class="login-page__theme-toggle-icon"
        :icon="isDarkMode ? 'mdi-white-balance-sunny' : 'mdi-moon-waning-crescent'"
      />
    </button>

    <div class="login-page__bg" aria-hidden="true">
      <div class="login-page__bg-base"></div>
      <div class="login-page__aurora"></div>
      <div class="login-page__stars"></div>
      <div class="login-page__grid-overlay"></div>
      <div class="login-page__orbit-ring login-page__orbit-ring--1"></div>
      <div class="login-page__orbit-ring login-page__orbit-ring--2"></div>
      <div class="login-page__scan-beam"></div>
      <div class="login-page__floaters">
        <div
          v-for="(floater, index) in loginFloaters"
          :key="floater.icon"
          class="login-page__floater"
          :class="'login-page__floater--' + (index + 1)"
        >
          <v-icon :icon="floater.icon" class="login-page__floater-icon" />
        </div>
      </div>
    </div>

    <div class="login-page__layout">
      <section class="login-page__showcase">
        <div class="login-page__glass-panel">
          <div class="login-page__brand-row">
            <img
              class="login-page__brand-logo"
              :src="brandLogoSrc"
              alt="Vozcare"
            />
            <span class="login-page__platform-pill">
              <span class="login-page__platform-dot"></span>
              CRM + Dialer Platform
            </span>
          </div>

          <div class="login-page__hero">
            <h1 class="login-page__headline">
              {{ loginHero.headline }}
              <span class="login-page__headline-accent">{{
                loginHero.headlineAccent
              }}</span>
            </h1>
            <p class="login-page__subtitle">{{ loginHero.subtitle }}</p>
          </div>

          <div class="login-page__tags">
            <span
              v-for="tag in loginTags"
              :key="tag.label"
              class="login-page__tag"
            >
              <v-icon :icon="tag.icon" class="login-page__tag-icon" />
              {{ tag.label }}
            </span>
          </div>

          <div class="login-page__showcase-row">
            <div class="login-page__features-grid">
              <article
                v-for="feat in loginFeatures"
                :key="feat.title"
                class="login-page__feat"
              >
                <v-icon :icon="feat.icon" class="login-page__feat-icon" />
                <h3 class="login-page__feat-title">{{ feat.title }}</h3>
                <p class="login-page__feat-tag">{{ feat.tag }}</p>
              </article>
            </div>

            <div class="login-page__portal" aria-hidden="true">
              <div class="login-page__portal-ring">
                <v-icon
                  icon="mdi-view-dashboard-outline"
                  class="login-page__portal-icon"
                />
              </div>
              <span class="login-page__portal-label">Admin Portal</span>
              <div class="login-page__portal-status">
                <span class="login-page__portal-status-dot"></span>
                Portal online
              </div>
              <div class="login-page__waveform">
                <span
                  v-for="n in 7"
                  :key="n"
                  class="login-page__wave-bar"
                ></span>
              </div>
            </div>
          </div>

          <div class="login-page__footer-stats">
            <div
              v-for="stat in loginFooterStats"
              :key="stat.label"
            >
              <div class="login-page__footer-stat-value">{{ stat.value }}</div>
              <div class="login-page__footer-stat-label">{{ stat.label }}</div>
            </div>
          </div>
        </div>
      </section>

      <aside class="login-page__form-panel">
        <div class="login-page__card-wrap">
          <div class="login-page__card-glow" aria-hidden="true"></div>
          <v-card class="login-page__card" elevation="0">
            <div class="login-page__card-accent" aria-hidden="true"></div>
            <div class="login-page__card-inner">
              <header class="login-page__card-header">
                <span class="login-page__card-badge">
                  <v-icon icon="mdi-shield-account-outline" />
                </span>
                <div class="login-page__card-header-text">
                  <p class="login-page__welcome">
                    Welcome back <span class="login-page__wave">👋</span>
                  </p>
                  <p class="login-page__welcome-sub">
                    Sign in to continue to your admin workspace
                  </p>
                </div>
              </header>
              <div class="login-page__divider"></div>
              <v-window v-model="loginStepper" class="login-page__window">
                  <v-window-item :value="1">
                    <v-card-text class="login-page__form-body">
                      <v-form ref="formSignIn" @submit.prevent lazy-validation>
                        <v-text-field
                          flat
                          hide-details="auto"
                          density="comfortable"
                          variant="outlined"
                          label="Email Address *"
                          class="login-page__field textVariant3"
                          @keydown.space="preventLeadingSpace"
                          @keydown.enter="validateSignIn()"
                          prepend-inner-icon="mdi-email-outline"
                          :rules="[(v) => !!v || 'Email Address is required']"
                          v-model="Login.EmailID"
                        ></v-text-field>
                        <v-text-field
                          flat
                          hide-details="auto"
                          density="comfortable"
                          variant="outlined"
                          label="Password *"
                          class="login-page__field textVariant3"
                          :type="isPwdLogin ? 'text' : 'password'"
                          @click:append-inner="isPwdLogin = !isPwdLogin"
                          @keydown.space="preventLeadingSpace"
                          @keydown.enter="validateSignIn()"
                          required
                          :append-inner-icon="
                            isPwdLogin ? 'mdi-eye' : 'mdi-eye-off'
                          "
                          prepend-inner-icon="mdi-lock-outline"
                          :rules="[(v) => !!v || 'Password is required']"
                          v-model="Login.Password"
                        ></v-text-field>
                      </v-form>
                    </v-card-text>
                    <div class="login-page__form-row">
                      <button
                        type="button"
                        class="login-page__remember"
                        :class="{ 'login-page__remember--on': rememberme }"
                        :disabled="Login.EmailID === '' && Login.Password === ''"
                        @click="toggleRememberMe"
                      >
                        <span class="login-page__remember-switch" aria-hidden="true">
                          <span class="login-page__remember-knob"></span>
                        </span>
                        <span class="login-page__form-row-text">Remember me</span>
                      </button>
                      <button
                        type="button"
                        class="login-page__form-row-link"
                        @click="loginStepper = 3"
                      >
                        Forgot password?
                      </button>
                    </div>
                    <v-card-actions class="login-page__card-actions">
                      <v-btn
                        block
                        :loading="loading"
                        variant="flat"
                        class="login-page__sign-in-btn fontStyle"
                        @click="validateSignIn()"
                      >
                        Sign In
                      </v-btn>
                    </v-card-actions>
                  </v-window-item>

                  <v-window-item :value="2">
                    <v-card-text class="login-page__form-body">
                      <v-form ref="formCP" lazy-validation>
                        <v-text-field
                          flat
                          hide-details="auto"
                          density="comfortable"
                          variant="outlined"
                          class="login-page__field textVariant3"
                          label="New Password *"
                          :type="isPwdNew ? 'text' : 'password'"
                          @click:append-inner="isPwdNew = !isPwdNew"
                          :append-inner-icon="isPwdNew ? 'mdi-eye' : 'mdi-eye-off'"
                          prepend-inner-icon="mdi-lock-outline"
                          :rules="[(v) => !!v || 'New Password is required']"
                          v-model="Password.NewPassword"
                        ></v-text-field>
                        <v-text-field
                          flat
                          hide-details="auto"
                          density="comfortable"
                          variant="outlined"
                          class="login-page__field textVariant3"
                          label="Confirm Password *"
                          :type="isPwdConfirm ? 'text' : 'password'"
                          @click:append-inner="isPwdConfirm = !isPwdConfirm"
                          :append-inner-icon="
                            isPwdConfirm ? 'mdi-eye' : 'mdi-eye-off'
                          "
                          prepend-inner-icon="mdi-lock-outline"
                          :rules="[(v) => !!v || 'Confirm Password is required']"
                          v-model="Password.ConfirmPassword"
                        ></v-text-field>
                      </v-form>
                    </v-card-text>
                    <v-card-actions class="login-page__card-actions">
                      <v-btn
                        variant="flat"
                        block
                        :loading="loading"
                        class="login-page__sign-in-btn fontStyle"
                        @click="ValidateConfirmPasswordForm()"
                      >
                        Confirm Password
                      </v-btn>
                    </v-card-actions>
                    <div class="login-page__back-btn-wrap">
                      <v-btn
                        density="compact"
                        variant="text"
                        class="login-page__forgot-link text-capitalize"
                        @click="loginStepper = 1"
                      >
                        Back to Sign In
                      </v-btn>
                    </div>
                  </v-window-item>

                  <v-window-item :value="3">
                    <v-card-text class="login-page__form-body">
                      <v-form ref="formResetPasswordSendOTP">
                        <div class="login-page__form-hint">
                          Please enter your registered Email
                        </div>
                        <v-text-field
                          flat
                          hide-details="auto"
                          density="comfortable"
                          variant="outlined"
                          label="Email Address"
                          v-model="ResetPassword.EmailID"
                          :rules="[(v) => !!v || 'Email Address is required']"
                          class="login-page__field mt-3"
                        ></v-text-field>
                      </v-form>
                    </v-card-text>
                    <v-card-actions class="login-page__card-actions">
                      <v-btn
                        block
                        :loading="loading"
                        variant="flat"
                        class="login-page__sign-in-btn"
                        @click="ValidateResetPassword()"
                      >
                        Next
                      </v-btn>
                    </v-card-actions>
                    <div class="login-page__back-btn-wrap">
                      <v-btn
                        density="compact"
                        variant="text"
                        class="login-page__forgot-link text-capitalize"
                        @click="loginStepper = 1"
                      >
                        Back to Sign In
                      </v-btn>
                    </div>
                  </v-window-item>

                  <v-window-item :value="4">
                    <v-card-text class="login-page__form-body">
                      <v-form ref="formConfirmOTP">
                        <div class="login-page__form-hint">
                          Enter the Vozcare Activation Code sent to your Email
                        </div>
                        <v-text-field
                          flat
                          hide-details="auto"
                          density="comfortable"
                          variant="outlined"
                          label="Activation Code *"
                          v-model="ResetPassword.ActivationCode"
                          :rules="[(v) => !!v || 'Activation Code is required']"
                          class="login-page__field mt-3"
                        ></v-text-field>
                        <v-text-field
                          flat
                          hide-details="auto"
                          density="comfortable"
                          variant="outlined"
                          label="New Password *"
                          v-model="ResetPassword.Password"
                          :rules="[(v) => !!v || 'New Password is required']"
                          class="login-page__field"
                          :type="isPwdConfirmCode ? 'text' : 'password'"
                          @click:append-inner="isPwdConfirmCode = !isPwdConfirmCode"
                          :append-inner-icon="
                            isPwdConfirmCode ? 'mdi-eye' : 'mdi-eye-off'
                          "
                          prepend-inner-icon="mdi-lock-outline"
                        ></v-text-field>
                        <v-text-field
                          flat
                          hide-details="auto"
                          density="comfortable"
                          variant="outlined"
                          label="Confirm Password *"
                          v-model="ResetPassword.ConfirmPassword"
                          :rules="[(v) => !!v || 'Confirm Password is required']"
                          class="login-page__field"
                          :type="isPwdNewConfirmCode ? 'text' : 'password'"
                          @click:append-inner="
                            isPwdNewConfirmCode = !isPwdNewConfirmCode
                          "
                          :append-inner-icon="
                            isPwdNewConfirmCode ? 'mdi-eye' : 'mdi-eye-off'
                          "
                          prepend-inner-icon="mdi-lock-outline"
                        ></v-text-field>
                      </v-form>
                    </v-card-text>
                    <v-card-actions class="login-page__card-actions">
                      <v-btn
                        block
                        :loading="loading"
                        variant="flat"
                        class="login-page__sign-in-btn fontStyle"
                        @click="ValidateResetPasswordFinalMethod()"
                      >
                        Reset Password
                      </v-btn>
                    </v-card-actions>
                    <div class="login-page__back-btn-wrap">
                      <v-btn
                        density="compact"
                        variant="text"
                        class="login-page__forgot-link text-capitalize"
                        @click="loginStepper = 1"
                      >
                        Back to Sign In
                      </v-btn>
                    </div>
                  </v-window-item>

                  <v-window-item :value="5">
                    <v-row no-gutters class="login-page__form-body">
                      <v-col cols="12" align="center">
                        <v-img max-width="180px" src="@/assets/logout.gif" />
                      </v-col>
                      <v-col cols="12" align="center" class="pt-4">
                        <div class="login-page__form-hint">
                          You have been logged out
                        </div>
                      </v-col>
                      <v-col cols="12" align="center" class="pt-1">
                        <div class="textVariant5">
                          Thank you for using
                          <span class="font-weight-bold">Vozcare</span>
                        </div>
                      </v-col>
                      <v-col cols="12" align="center" class="pt-4 pb-4">
                        <v-btn
                          block
                          variant="flat"
                          class="login-page__logout-btn text-capitalize"
                          @click="loginStepper = 1"
                        >
                          Sign In
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-window-item>
                </v-window>
            </div>
          </v-card>
        </div>
      </aside>
    </div>
  </div>
</template>

<script>
import "@/assets/styles/login-page.css";
import Snackbar from "@/components/Extras/Snackbar.vue";
import {
  loginHero,
  loginTags,
  loginFeatures,
  loginFooterStats,
  loginFloaters,
} from "@/data/loginContent.js";
import { cognitoAPIs } from "@/mixins/CognitoAPIs/cognitoAPIs.mjs";

export default {
  components: {
    Snackbar,
  },
  props: {
    useDisplaySize: Object,
  },
  mixins: [cognitoAPIs],
  data: () => ({
    loginHero,
    loginTags,
    loginFeatures,
    loginFooterStats,
    loginFloaters,
    isDarkMode: false,
    loginStepper: 1,

    loading: false,
    isPwdNew: false,
    isPwdLogin: false,
    isPwdConfirm: false,
    isPwdConfirmCode: false,
    isPwdNewConfirmCode: false,
    rememberme: false,

    session: {},

    Login: {
      EmailID: "",
      Password: "",
    },
    Password: {
      NewPassword: "",
      ConfirmPassword: "",
    },
    ResetPassword: {
      EmailID: "",
      ActivationCode: "",
      Password: "",
      ComfirmPassword: "",
    },
    sign_up: {
      user_name: "",
      user_email_id: "",
    },

    SnackBarComponent: {},
    countryCodeItems: [
      { country_name: "India", country_code: "+91" },
      { country_name: "USA", country_code: "+1" },
    ],
  }),
  watch: {
    model(val) {
      if (this.cardContent.length == val) {
        this.clearItervalMethod();
      }
    },
  },
  computed: {
    brandLogoSrc() {
      return this.isDarkMode
        ? require("@/assets/vozcare_dark_logo.png")
        : require("@/assets/vozcare_light_logo.png");
    },
  },
  mounted() {
    this.initLoginTheme();
    this.CheckRememberMe();
    this.disableHistoryChanges();
  },
  methods: {
    initLoginTheme() {
      const saved = localStorage.getItem("loginPageTheme");
      if (saved === "dark") {
        this.isDarkMode = true;
      } else if (saved === "light") {
        this.isDarkMode = false;
      } else {
        this.isDarkMode = false;
      }
    },
    toggleLoginTheme() {
      this.isDarkMode = !this.isDarkMode;
      localStorage.setItem(
        "loginPageTheme",
        this.isDarkMode ? "dark" : "light"
      );
    },
    disableHistoryChanges() {
      window.history.pushState(null, "", window.location.href);
      window.onpopstate = function () {
        window.history.pushState(null, "", window.location.href);
      };
    },
    async validateSignUpMethod() {
      const { valid } = await this.$refs.formSignUp.validate();
      if (valid) {
        this.signUpMethod();
      } else {
        this.SnackBarComponent = {
          SnackbarVmodel: true,
          SnackbarColor: "error",
          Top: true,
          SnackbarText: "Invalid Input",
        };
      }
    },
    preventLeadingSpace(e) {
      if (!e.target.value) e.preventDefault();
      else if (e.target.value[0] == " ")
        e.target.value = e.target.value.replace(/^\s*/, "");
    },
    AddRememberMe() {
      if (this.rememberme == true) {
        localStorage.setItem("currentuseremail", this.Login.EmailID.trim());
        localStorage.setItem("currentuserpassword", this.Login.Password.trim());
        localStorage.setItem("rememberme", this.rememberme);
      } else {
        localStorage.setItem("currentuseremail", null);
        localStorage.setItem("currentuserpassword", null);
        localStorage.setItem("rememberme", this.rememberme);
      }
    },
    toggleRememberMe() {
      if (this.Login.EmailID === "" && this.Login.Password === "") return;
      this.rememberme = !this.rememberme;
    },
    CheckRememberMe() {
      if (localStorage.getItem("rememberme") == "true") {
        this.Login.EmailID = localStorage.getItem("currentuseremail")
          ? localStorage.getItem("currentuseremail")
          : "";
        this.Login.Password = localStorage.getItem("currentuserpassword")
          ? localStorage.getItem("currentuserpassword")
          : "";
        this.rememberme = true;
      }
    },
  },
};
</script>
